import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { ColDef } from "ag-grid-community";
import {
  EvaluatedResults,
  IAMUserState,
  JsonSchema,
  LandingPageOptions,
  PrimitiveValueType,
  ReadRecordRequest,
  RowData,
  RowDataReducerAction,
  SetupListNavInfoByDepartment,
  ToastInfo,
} from "../types";
import {
  getColDefs,
  getDepartmentIdByStore,
  getNewRow,
  getTableData,
  getUpdatedNewEntryRowData,
  setEvaluationResultsIfNeeded,
} from "../pages/LandingPage/helpers";
import {
  DEFAULT_OPTION,
  DEPARTMENTS,
  TOAST_MESSAGES,
  THIRD_PARTY_SETUPS,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../pages/LandingPage/constants";

interface UseRefetchTableDataParams {
  setupBaseRequestInfo: ReadRecordRequest;
  headerLabel: string | undefined;
  landingPageOptions: LandingPageOptions;
  iamUserState: IAMUserState;
  evaluatedResults: EvaluatedResults;
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
  dispatch: React.Dispatch<RowDataReducerAction>;
  setTableTitleInfo: ((title: string) => void) | undefined;
  setCurrentPage: (pageNumber: number) => void;
  setSearchValue: (value: string) => void;
  setPageSizeOption: (option: { label: string; value: number }) => void;
  setIsRecordOpen: (isRecordOpen: boolean) => void;
  setActiveTabIndex: (index: number) => void;
  setSelectedTabSectionKey: (key: string | undefined) => void;
  setSelectedDashboardTabSectionKey: (key: string | undefined) => void;
  setEvaluatedResults: React.Dispatch<React.SetStateAction<EvaluatedResults>>;
  setIsSideSheetOpen: (isOpen: boolean) => void;
  setLandingPageToast: (toastInfo: ToastInfo) => void;
  setSelected: React.Dispatch<React.SetStateAction<LandingPageOptions>>;
  setChipFilterValue: React.Dispatch<
    React.SetStateAction<PrimitiveValueType[]>
  >;
}

interface TableData {
  columnDefs: ColDef[];
  rowDataList: RowData[];
  schema: JsonSchema;
}

export function useRefetchTableData({
  setupBaseRequestInfo,
  headerLabel,
  landingPageOptions,
  iamUserState,
  evaluatedResults,
  setupListNavInfoByDepartment,
  dispatch,
  setTableTitleInfo,
  setCurrentPage,
  setSearchValue,
  setIsRecordOpen,
  setPageSizeOption,
  setActiveTabIndex,
  setSelectedTabSectionKey,
  setSelectedDashboardTabSectionKey,
  setEvaluatedResults,
  setIsSideSheetOpen,
  setLandingPageToast,
  setSelected,
  setChipFilterValue,
}: UseRefetchTableDataParams): UseQueryResult<TableData> {
  const { store, department, setup, groupId } = landingPageOptions;

  const isTableDataEnabled =
    !!setup.value &&
    !!store.value &&
    !!department.value &&
    !THIRD_PARTY_SETUPS.includes(setup.value) &&
    (department.value !== DEPARTMENTS.ACCOUNTING || !!groupId.value) &&
    ((department.value !== DEPARTMENTS.PARTS &&
      department.value !== DEPARTMENTS.SERVICE) ||
      !!landingPageOptions.departmentId.value);

  const resetTableState = (): void => {
    setActiveTabIndex(0);
    setIsSideSheetOpen(false);
    setSelectedTabSectionKey(undefined);
    setSelectedDashboardTabSectionKey(undefined);
    setEvaluatedResults({});
    setCurrentPage(1);
    setSearchValue("");
    setPageSizeOption(DEFAULT_ROWS_PER_PAGE_OPTIONS[2]);
    setChipFilterValue([]);
    setIsRecordOpen(false);
  };

  const getUpdatedRequestInfo = (): ReadRecordRequest => ({
    ...setupBaseRequestInfo,
    storeId: store.value,
    functionalArea: department.value,
    functionalUnitName: setup.value,
    departmentId: getDepartmentIdByStore({
      store: store.value,
      department: department.value,
      extendedAccountInfo: iamUserState.extendedAccountInfo,
      setupListNavInfoByDepartment,
    }),
    groupId: groupId.value,
  });

  const handleSchemaError = (schema: JsonSchema): TableData => {
    setLandingPageToast(TOAST_MESSAGES.SCHEMA_ERROR(setup.label));

    setSelected((prev) => ({
      ...prev,
      department: DEFAULT_OPTION,
      departmentId: DEFAULT_OPTION,
      setup: DEFAULT_OPTION,
    }));

    return {
      columnDefs: [],
      rowDataList: [],
      schema,
    };
  };

  return useQuery({
    queryKey: ["refetchTableData", setup.value, department.value],

    queryFn: async (): Promise<TableData> => {
      setTableTitleInfo?.("Loading...");

      const updatedSetupBaseRequestInfo = getUpdatedRequestInfo();

      resetTableState();

      const { rowDataList, schema } = await getTableData(
        updatedSetupBaseRequestInfo,
      );

      if (!schema.sections) {
        return handleSchemaError(schema);
      }

      const columnDefs = getColDefs(schema);

      setTableTitleInfo?.(`${headerLabel} (${rowDataList.length})`);

      const newRowData = await getUpdatedNewEntryRowData({
        schema,
        rowData: getNewRow(schema),
        store: store.value,
        department: department.value,
        extendedAccountInfo: iamUserState.extendedAccountInfo,
        accountName: setupBaseRequestInfo.accountName,
        setupBaseRequestInfo: updatedSetupBaseRequestInfo,
        setupListNavInfoByDepartment,
      });

      await setEvaluationResultsIfNeeded({
        setupBaseRequestInfo: updatedSetupBaseRequestInfo,
        evaluatedResults,
        setEvaluatedResults,
        iamUserState,
        setupListNavInfoByDepartment,
      });

      dispatch({
        type: "UPDATE_ROW_DATA_REDUCER_STATE",
        payload: {
          schema,
          rowData: newRowData,
          rowDataList,
          columnDefs,
          rowDataErrors: {},
          resetState: {
            newEntryRowData: newRowData,
            schema,
            rowData: newRowData,
            rowDataList,
            dialogRowData: {},
          },
          dialogRowData: {},
          isReadOnly: false,
        },
      });

      return {
        columnDefs,
        rowDataList,
        schema,
      };
    },

    enabled: isTableDataEnabled,
    staleTime: 0,
  });
}
